import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Loader2 } from "lucide-react";
import SiteShell from "../components/SiteShell";
import HoverChat from "../components/HoverChat";
import { bookVisit, trackAction } from "../lib/api";
import { Field, inputStyle, primaryButtonStyle } from "./audit/StepEnergy";

/**
 * `/visit` — Schedule a factory / R&D tour.
 * `?visit_type=` preselects the visit type (used by Dharshana community cells
 * and the Partners confirmation).
 */
const VISIT_TYPES = [
  { v: "factory-tour", label: "Factory tour" },
  { v: "rd-visit", label: "R&D visit" },
  { v: "media", label: "Media" },
  { v: "talent", label: "Talent" },
  { v: "griddata-pilot", label: "griddata pilot (DISCOM / institution)" },
  { v: "partner-onboarding", label: "Partner onboarding" },
  { v: "community-cooperative", label: "Community cooperative" },
];
const SLOTS = ["10:00", "12:00", "14:00", "16:00"];

export default function Visit() {
  const [params] = useSearchParams();
  const initialType = VISIT_TYPES.some((t) => t.v === params.get("visit_type"))
    ? params.get("visit_type")
    : "factory-tour";

  const [f, setF] = useState({
    visit_type: initialType,
    slot_date: new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10),
    slot_time: "12:00",
    name: "", org: "", role_purpose: "", email: "", phone: "",
    party_size: 1, logistics_notes: "",
    discom_name: "", discom_state: "", scada_ami_status: "", substation_mva: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(null);
  const set = (p) => setF({ ...f, ...p });

  useEffect(() => {
    trackAction({ route: "book-tour", source: "visit", sourceState: "loaded", audience: initialType });
  }, [initialType]);

  const emailOk = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email);
  const isGriddata = f.visit_type === "griddata-pilot";
  const canSubmit = f.name.trim() && emailOk && f.phone.trim() && f.role_purpose.trim() &&
    f.party_size >= 1 && f.party_size <= 10 && f.slot_date && f.slot_time && !busy;

  const submit = async (e) => {
    e.preventDefault(); if (!canSubmit) return;
    setBusy(true); setErr("");
    try {
      const payload = { ...f, party_size: Number(f.party_size),
        substation_mva: f.substation_mva ? Number(f.substation_mva) : null };
      const data = await bookVisit(payload);
      setOk(data);
      trackAction({ route: "book-tour", source: "visit", sourceState: "submitted", audience: f.visit_type });
    } catch (ex) {
      setErr(ex?.response?.data?.detail || ex?.message || "Booking failed.");
    } finally { setBusy(false); }
  };

  return (
    <SiteShell>
      <section style={{ maxWidth: 940, margin: "0 auto",
        padding: "clamp(52px, 8vw, 100px) clamp(20px, 5vw, 60px) clamp(50px, 7vw, 90px)" }}
        data-testid="visit-page">
        <div className="gg-eyebrow" style={{ marginBottom: 10 }}>Schedule a tour</div>
        <h1 className="gg-hero-title" style={{ marginBottom: 18, fontSize: "clamp(32px, 5vw, 56px)", maxWidth: "22ch" }} data-testid="visit-headline">
          Walk into a facility that runs on its own sun.
        </h1>
        <p style={{ color: "var(--text-dim)", fontSize: 16.5, lineHeight: 1.7, maxWidth: 720 }} data-testid="visit-lede">
          Our Tamil Nadu Factory is powered by its own solar array and its own
          cells — transparency is the competitive advantage. Come see what
          energy sovereignty <em>feels like as an environment</em> before you
          see a single spec sheet. Ask about our{" "}
          <HoverChat term="griddata">griddata</HoverChat> pilot, our{" "}
          <HoverChat term="digital-twin">digital twin</HoverChat>, or just watch
          the line run.
        </p>

        {ok ? <Confirmation data={ok} /> : (
          <form onSubmit={submit} data-testid="visit-form" style={{ marginTop: 36, padding: "22px 24px 26px",
            borderRadius: "var(--radius-lg)",
            background: "linear-gradient(180deg, rgba(255,255,255,0.05), rgba(255,255,255,0.01))",
            border: "1px solid var(--line-strong)" }}>
            {/* Step 1 — Visit type */}
            <div className="gg-eyebrow" style={{ marginBottom: 10 }}>Step 1 · What kind of visit</div>
            <div data-testid="visit-types" style={{ display: "grid", gap: 8,
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))" }}>
              {VISIT_TYPES.map((t) => {
                const active = f.visit_type === t.v;
                return <button key={t.v} type="button" onClick={() => set({ visit_type: t.v })} data-testid={`visit-type-${t.v}`}
                  style={{ padding: "10px 12px", borderRadius: 10,
                    background: active ? "linear-gradient(180deg, rgba(244,169,31,0.14), rgba(244,169,31,0.02))" : "rgba(255,255,255,0.03)",
                    border: `1px solid ${active ? "rgba(244,169,31,0.5)" : "var(--line)"}`,
                    color: active ? "var(--gold-soft)" : "var(--text-cloud)",
                    fontFamily: "inherit", fontSize: 13.5, textAlign: "left", cursor: "pointer" }}>{t.label}</button>;
              })}
            </div>

            {/* Step 2 — Slot */}
            <div className="gg-eyebrow" style={{ marginTop: 20, marginBottom: 10 }}>Step 2 · When</div>
            <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))" }}>
              <Field label="Date"><input type="date" value={f.slot_date} onChange={(e) => set({ slot_date: e.target.value })} data-testid="visit-input-date" style={inputStyle} min={new Date().toISOString().slice(0, 10)} /></Field>
              <Field label="Slot (IST)">
                <select value={f.slot_time} onChange={(e) => set({ slot_time: e.target.value })} data-testid="visit-input-time" style={inputStyle}>
                  {SLOTS.map((s) => <option key={s} value={s} style={{ background: "var(--night-2)" }}>{s}</option>)}
                </select>
              </Field>
            </div>
            <div style={{ marginTop: 6, color: "var(--text-mute)", fontSize: 12.5 }}>We'll confirm your slot within 1 business day.</div>

            {/* Step 3 — Details */}
            <div className="gg-eyebrow" style={{ marginTop: 20, marginBottom: 10 }}>Step 3 · Who</div>
            <div style={{ display: "grid", gap: 14, gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))" }}>
              <Field label="Full name*"><input required value={f.name} onChange={(e) => set({ name: e.target.value })} data-testid="visit-input-name" style={inputStyle} /></Field>
              <Field label="Organisation"><input value={f.org} onChange={(e) => set({ org: e.target.value })} data-testid="visit-input-org" style={inputStyle} /></Field>
              <Field label="Role / purpose*"><input required value={f.role_purpose} onChange={(e) => set({ role_purpose: e.target.value })} data-testid="visit-input-role" style={inputStyle} /></Field>
              <Field label="Email*"><input type="email" required value={f.email} onChange={(e) => set({ email: e.target.value })} data-testid="visit-input-email" style={{ ...inputStyle, borderColor: f.email && !emailOk ? "rgba(255,120,120,0.5)" : "var(--line)" }} /></Field>
              <Field label="Phone*"><input required value={f.phone} onChange={(e) => set({ phone: e.target.value })} data-testid="visit-input-phone" style={inputStyle} /></Field>
              <Field label="Party size" hint="1 – 10 visitors."><input type="number" min={1} max={10} value={f.party_size} onChange={(e) => set({ party_size: parseInt(e.target.value, 10) || 1 })} data-testid="visit-input-party" style={inputStyle} /></Field>
            </div>
            <div style={{ marginTop: 14 }}>
              <Field label="Accessibility / logistics notes"><textarea rows={3} value={f.logistics_notes} onChange={(e) => set({ logistics_notes: e.target.value })} data-testid="visit-input-notes" style={{ ...inputStyle, resize: "vertical", minHeight: 72, fontFamily: "inherit" }} /></Field>
            </div>

            {/* griddata pilot tailored intake */}
            {isGriddata && (
              <div data-testid="visit-griddata-intake" style={{ marginTop: 18, padding: 16, borderRadius: 12,
                background: "rgba(124,201,255,0.06)", border: "1px solid rgba(124,201,255,0.3)" }}>
                <div style={{ fontFamily: "Fraunces, Georgia, serif", fontSize: 17, color: "var(--paper)", marginBottom: 6 }}>
                  One substation, twelve months.
                </div>
                <div style={{ color: "var(--text-dim)", fontSize: 13, marginBottom: 12 }}>
                  Tell us your substation, and we'll bring the OS. RDSS-aligned pilot.
                </div>
                <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))" }}>
                  <Field label="DISCOM / institution"><input value={f.discom_name} onChange={(e) => set({ discom_name: e.target.value })} data-testid="visit-input-discom" style={inputStyle} /></Field>
                  <Field label="State"><input value={f.discom_state} onChange={(e) => set({ discom_state: e.target.value })} data-testid="visit-input-discom-state" style={inputStyle} /></Field>
                  <Field label="SCADA / AMI status"><input value={f.scada_ami_status} onChange={(e) => set({ scada_ami_status: e.target.value })} data-testid="visit-input-scada" placeholder="e.g. SCADA @ 33kV level, no AMI" style={inputStyle} /></Field>
                  <Field label="Substation MVA"><input type="number" value={f.substation_mva} onChange={(e) => set({ substation_mva: e.target.value })} data-testid="visit-input-mva" style={inputStyle} /></Field>
                </div>
              </div>
            )}

            {err && <p data-testid="visit-error" style={{ marginTop: 12, color: "#ff8a8a", fontSize: 13 }}>{err}</p>}

            <div style={{ marginTop: 22, display: "flex", justifyContent: "flex-end" }}>
              <button type="submit" disabled={!canSubmit} data-testid="visit-submit" style={primaryButtonStyle(!canSubmit)}>
                {busy ? <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><Loader2 size={14} className="animate-spin" /> Requesting…</span> : "Request this slot"}
              </button>
            </div>
          </form>
        )}
      </section>
    </SiteShell>
  );
}

function Confirmation({ data }) {
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} data-testid="visit-confirmation"
      style={{ marginTop: 36, padding: 26, borderRadius: "var(--radius-lg)",
        background: "linear-gradient(180deg, rgba(31,170,89,0.14), rgba(31,170,89,0.02))",
        border: "1px solid rgba(31,170,89,0.4)" }}>
      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 8 }}>
        <Check size={18} color="var(--green-bright)" />
        <span className="gg-eyebrow" style={{ color: "var(--green-bright)", margin: 0 }}>Slot requested</span>
      </div>
      <h3 className="gg-h2">{data.message}</h3>
      <p style={{ marginTop: 12, fontFamily: "Fraunces, Georgia, serif", fontSize: 20, color: "var(--gold-soft)" }}>
        Reference: <span data-testid="visit-ref-id">{data.ref_id}</span>
      </p>
    </motion.div>
  );
}
